import React from "react";

import Button from "ui/Button";
import { $PrimaryButton } from "ui/Button/styled-components";
import Alert from "ui/Alert";
import Typography from "ui/Typography";
import { envWithDefault } from "utils/env";

import styled from "styled-components/macro";

const $Wrapper = styled.div`
  margin-top: 1rem;
`;

const $ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;

  a.btn {
    min-width: 210px;
  }
`;

const $GenerateForm = styled.form`
  display: grid;
  grid-template-columns: 3fr 1fr;
  grid-gap: 1rem;
  margin-top: 1rem;
`;

const $FormLabel = styled.label`
  text-align: right;
`;

const $FormField = styled.div`
  text-align: left;
`;

const $FormButton = styled($PrimaryButton)`
  grid-column: 1 / span 2;
`;

type DevLoginProps = {
  isOnDarkBackground: boolean;
  isEducatorSignup?: boolean;
  connectCode?: string;
};

function getQueryParams(
  isEducatorSignup: boolean,
  connectCode: string,
  isExpeditedOnboarding: boolean,
): string {
  if (isExpeditedOnboarding) {
    return "?intent=expedited_educator_onboarding";
  } else if (isEducatorSignup) {
    return "?intent=educator_signup";
  } else if (connectCode) {
    return "?connect_code=" + connectCode;
  }
  return "";
}

function DevLogin({
  isOnDarkBackground,
  isEducatorSignup = false,
  connectCode = "",
}: DevLoginProps) {
  const params = new URLSearchParams(window.location.search);
  const loginError = params.get("error");
  if (loginError) {
    // clear the query param to dismiss the error
    window.history.pushState("clear", "", "?#");
  }
  const enableAdminLogin = envWithDefault("ENABLE_DEV_ADMIN_TOOLS") === "true";
  const isExpeditedOnboarding =
    params.get("intent") === "expedited_educator_onboarding";
  const queryParams = getQueryParams(
    isEducatorSignup,
    connectCode,
    isExpeditedOnboarding,
  );
  return (
    <$Wrapper>
      <Typography
        size="h4"
        color={isOnDarkBackground ? "ColorWhite" : "ColorSpace500"}
        className="mb-4"
      >
        Sign in as one of the test users:
      </Typography>

      {loginError && <Alert variant="danger">{loginError}</Alert>}
      <$ButtonWrapper>
        <Button
          analyticsName="clicked_dev_teacher_login"
          isOnDarkBackground={isOnDarkBackground}
          href="/auth/hogwarts"
          requestInProgress={false}
        >
          Hogwarts
        </Button>
      </$ButtonWrapper>
      <$ButtonWrapper>
        <Button
          analyticsName="clicked_dev_seed_cohort"
          isOnDarkBackground={isOnDarkBackground}
          href={"/auth/cohorts" + queryParams}
          requestInProgress={false}
        >
          Cohorts
        </Button>
      </$ButtonWrapper>
      <$ButtonWrapper>
        <Button
          analyticsName="clicked_dev_seed_demo"
          isOnDarkBackground={isOnDarkBackground}
          href="/auth/demos"
          requestInProgress={false}
        >
          Demos
        </Button>
      </$ButtonWrapper>
      <$ButtonWrapper>
        <Button
          analyticsName="clicked_dev_seed_demo"
          isOnDarkBackground={isOnDarkBackground}
          href={"/auth/clever_cohorts" + queryParams}
          requestInProgress={false}
        >
          Clever Cohorts
        </Button>
      </$ButtonWrapper>
      <$ButtonWrapper>
        <Button
          analyticsName="clicked_dev_seed_demo"
          isOnDarkBackground={isOnDarkBackground}
          href={"/auth/clever_sync_demo_schools" + queryParams}
          requestInProgress={false}
        >
          Clever Sync Demo School
        </Button>
      </$ButtonWrapper>
      {enableAdminLogin && (
        <$ButtonWrapper>
          <Button
            analyticsName="clicked_dev_admin"
            isOnDarkBackground={isOnDarkBackground}
            href="/admin/auth/dev"
            requestInProgress={false}
          >
            Admins
          </Button>
        </$ButtonWrapper>
      )}

      <Typography
        size="h4"
        color={isOnDarkBackground ? "ColorWhite" : "ColorSpace500"}
        className="mb-4 mt-5"
      >
        Or generate a custom cohort for testing:
      </Typography>

      <$GenerateForm
        action="/generate-custom-cohort"
        method="POST"
        id="new-cohort-form"
      >
        <$FormLabel htmlFor="teacher-count">Number of teachers:</$FormLabel>
        <$FormField>
          <input
            required
            id="teacher-count"
            type="number"
            name="teacher_count"
            defaultValue={1}
            min="1"
            max="10"
          />
        </$FormField>

        <$FormLabel htmlFor="student-count">Number of students:</$FormLabel>
        <$FormField>
          <input
            required
            id="student-count"
            type="number"
            name="student_count"
            defaultValue={1}
            min="1"
            max="60"
          />
        </$FormField>

        <$FormLabel htmlFor="initial-reflection">
          Start with a reflection?
        </$FormLabel>
        <$FormField>
          <input
            id="initial-reflection"
            type="checkbox"
            name="initial_reflection"
          />
        </$FormField>

        <$FormLabel htmlFor="is-clever-cohort">Clever cohort?</$FormLabel>
        <$FormField>
          <input id="is-clever-cohort" type="checkbox" name="clever_cohort" />
        </$FormField>

        <$FormButton id="new-cohort-button" type="submit">
          Generate Cohort
        </$FormButton>
      </$GenerateForm>
    </$Wrapper>
  );
}

export default DevLogin;
